import { FieldPath, FieldValues } from 'react-hook-form';
import { AddressFormValues } from 'components/address-form/types';
import { useGivebutterFormContext } from 'components/form';
import { UseGivebutterFormReturn } from 'components/form/useGivebutterForm';
import { Address } from 'types/domain';

const ADDRESS_SERVER_FIELDS = [
    'address_1',
    'address_2',
    'state',
    'zipcode',
    'city',
    'country',
] as Array<keyof Address>;

type AddressServerField = (typeof ADDRESS_SERVER_FIELDS)[number];

const useFormServerErrors = <T extends FieldValues>(methods?: UseGivebutterFormReturn<T>) => {
    const maybeFormContext = useGivebutterFormContext<T>(true);
    const genericMethods = methods ?? maybeFormContext;
    const setAddressErrors = (errors?: { [key: string]: string[] }) => {
        const { getValues, setError } =
            genericMethods as unknown as UseGivebutterFormReturn<AddressFormValues>;
        if (errors) {
            Object.keys(errors).forEach(key => {
                const splitPath = [...key.split('.').reverse()];
                const [unwrappedPath, ...prefixPathArr] = splitPath;
                const prefixPath = [...prefixPathArr].reverse().join('.');
                const getPath = curryPathPrefix(prefixPath);
                const normalizedKey = transformServerKeyToAddressFormKey(
                    unwrappedPath,
                    getValues(getPath('country')) as string,
                );

                setError(getPath(normalizedKey as FieldPath<AddressFormValues>), {
                    message: errors[key][0],
                });
            });
        }
    };

    return {
        setAddressErrors,
    };
};

const curryPathPrefix = (prefix: string) => (path: FieldPath<AddressFormValues>) => {
    if (prefix.length > 0) {
        return `${prefix}.${path}` as FieldPath<AddressFormValues>;
    }

    return path;
};

const transformServerKeyToAddressFormKey = (serverKey: string, country = 'USA') => {
    switch (serverKey) {
        case 'address_1':
            return 'line1';
        case 'address_2':
            return 'line2';
        case 'zipcode':
            return 'zip';
        case 'state':
            return country === 'USA' ? 'stateUs' : 'stateIntl';
        default:
            return serverKey;
    }
};

const checkAddressErrors = (errors: { [key: string]: string[] }) => {
    return Object.keys(errors).some(key =>
        ADDRESS_SERVER_FIELDS.includes(key as AddressServerField),
    );
};

export {
    useFormServerErrors,
    curryPathPrefix,
    transformServerKeyToAddressFormKey,
    checkAddressErrors,
};
